/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef, useEffect } from 'react';
import { Button, Row, Col, Form, Input, Typography, notification } from 'antd';
import _ from 'lodash';

// Helpers
import LocalStorageHelper from '../../helpers/local_storage';
import PasswordHelper from '../../helpers/password';

import Card from '../../components/Card';
import PixelHelper from '../../helpers/pixel';

const { Item } = Form;
const { Text } = Typography;

const SettingPage = (props) => {
	// States
	const formRef = useRef();
	const [showPasswordOne, setShowPasswordOne] = useState(false);
	const [showPasswordTwo, setShowPasswordTwo] = useState(false);
	const [showPasswordThree, setShowPasswordThree] = useState(false);
	const [disabled, setDisabled] = useState(true);
	const [match, setMatch] = useState(true);
	const [newPassword, setNewPassword] = useState(null);
	const [newPassValidationMsg, setNewPassValidationMsg] = useState(null);

	// Props
	const {
		onSubmit,
		settingPageProps,
		resetPage,
		resetMeta
	} = props;

	// Other Variables
	const user = LocalStorageHelper.getUser();
	const { meta, loading, loadingSubmit } = settingPageProps;
	const { name, email, login_method } = user || {};

	useEffect(() => {
		PixelHelper.pageView(window.location.pathname);
		return () => resetPage();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (meta) {
			const type = meta.code === 200 ? 'success' : 'error';
			if (!meta?.message?.toLowerCase().includes('password')) {
				notification[type]({
					message: meta.message,
					placement: 'bottomRight'
				});
				// Reset page meta
				resetMeta();
			}

			if (type === 'success') {
				setDisabled(true);
				setMatch(true);
				setNewPassValidationMsg(null);
				setNewPassword(null);
				formRef.current.setFieldsValue({
					current_password: undefined,
					new_password: undefined,
					confirm_password: undefined
				});
				setShowPasswordOne(false);
				setShowPasswordTwo(false);
				setShowPasswordThree(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meta]);

	useEffect(() => {
		if (newPassword && newPassword.length < 8) {
			setNewPassValidationMsg('Password must be atleast 8 characters.');
		} else if (newPassword && !PasswordHelper.validatePassword(newPassword)) {
			setNewPassValidationMsg('The password must contain at least one uppercase letter, one lowercase letter, one number, and one special character from the following set: _!@#$%^&*(),.?":{}|<>.');
		} else {
			setNewPassValidationMsg(null);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newPassword]);

	// ---------------------------------- Start of Functions ---------------------------------- //
	const toggleShowPassword = (type) => {
		if (type === 'current') {
			setShowPasswordOne(prevShowPassword => !prevShowPassword);
		} else if (type === 'new') {
			setShowPasswordTwo(prevShowPassword => !prevShowPassword);
		} else if (type === 'confirm') {
			setShowPasswordThree(prevShowPassword => !prevShowPassword);
		}
	};

	const checkFormValues = () => {
		const currentPasswordValue = formRef.current.getFieldValue('current_password');
		const newPasswordValue = formRef.current.getFieldValue('new_password');
		const confirmNewPasswordValue = formRef.current.getFieldValue('confirm_password');

		const isCurrentPasswordProvided = currentPasswordValue !== undefined && currentPasswordValue !== '';

		if (
			formRef.current.getFieldValue('name') &&
			(!isCurrentPasswordProvided || (isCurrentPasswordProvided && newPasswordValue && confirmNewPasswordValue)) &&
			(newPasswordValue === confirmNewPasswordValue)
		) {
			setDisabled(false);
		} else {
			setDisabled(true);
		}
	};


	const handleOnSubmit = async (values) => {
		const { name, confirm_password, current_password, new_password } = values;
		const fields = {
			name,
			current_password,
			confirm_password,
			new_password
		};
		onSubmit(fields);
	};

	const handleValidationMsg = (fieldName) => {
		if (meta && meta?.code !== 200 && fieldName === 'current_password') {
			return (
				<Text
					type="danger"
					style={{ fontSize: 16 }}
				>
					{meta.message === 'Current password is incorrect.' ? meta?.message : ''}
				</Text>
			);
		}

		if (meta && meta?.code !== 200 && fieldName !== 'current_password') {
			return (
				<Text
					type="danger"
					style={{ fontSize: 16 }}
				>
					{meta.message !== 'Current password is incorrect.' ? meta?.message : ''}
				</Text>
			);
		}

		if (fieldName === 'new_password') {
			if (newPassValidationMsg) {
				return (
					<Text
						type="danger"
						style={{ fontSize: 16 }}
					>
						{newPassValidationMsg}
					</Text>);
			}
		}
		return null;
	};

	// ---------------------------------- End of Functions ---------------------------------- //
	return (
		<div>
			<Row>
				<Col
					span={24}
					style={{ marginBottom: 15 }}
				>
					<Card
						title="Account Settings"
						loading={loading}
						style={{ width: '100%' }}
					>
						<Row>
							<Col
								xxl={{ span: 9, offset: 2 }}
								lg={{ span: 9, offset: 2 }}
								md={{ span: 9, offset: 2 }}
								sm={{ span: 24 }}
								xs={{ span: 24 }}
							>
								<Form
									style={{ width: '100%' }}
									ref={formRef}
									layout="vertical"
									initialValues={{
										name,
										email
									}}
									onFinish={handleOnSubmit}
								>
									<Item
										name="name"
										label="Name"
										rules={[
											{ message: 'Name is required', required: true }
										]}
										onChange={checkFormValues}
									>
										<Input />
									</Item>
									<Item
										name="email"
										label="Email"
										rules={[
											{ message: 'Email is required', required: true },
											{ type: 'email', message: 'Email is not valid' }
										]}
										onChange={checkFormValues}
									>
										<Input disabled />
									</Item>
									<Item
										name="current_password"
										label="Current Password"
										rules={[
											{ message: 'Current Password is required', required: false }
										]}
										onChange={checkFormValues}
										help={handleValidationMsg('current_password')}
									>
										<Input.Password
											placeholder="Enter Current Password"
											disabled={login_method === 'google' || loadingSubmit || !user}
											visibilityToggle={{ visible: showPasswordOne, onVisibleChange: () => toggleShowPassword('current') }}
										/>
									</Item>
									<Item
										name="new_password"
										label="New Password"
										rules={[
											{
												required: formRef.current?.getFieldValue('current_password'),
												message: 'New Password is required'
											}
										]}
										help={handleValidationMsg('new_password')}
									>
										<Input.Password
											placeholder="Enter New Password"
											onChange={(e) => {
												let confirmPassword = formRef.current.getFieldValue('confirm_password');
												setNewPassword(e.target.value);
												if (e.target.value) {
													if (confirmPassword) {
														if (e.target.value && e.target.value !== confirmPassword) {
															setMatch(false);
														} else {
															setMatch(true);
														}
													}
												} else {
													setMatch(true);
												}
												checkFormValues();
											}}
											disabled={login_method === 'google' || loadingSubmit || !user}
											visibilityToggle={{ visible: showPasswordTwo, onVisibleChange: () => toggleShowPassword('new') }}
										/>
									</Item>
									<Item
										name="confirm_password"
										label="Confirm Password"
										rules={[
											{
												required: formRef.current?.getFieldValue('current_password'),
												message: 'Confirm Password is required'
											}
										]}
										help={!match ? <Text type="danger">The password did not match!</Text> : handleValidationMsg(null)}
									>
										<Input.Password
											placeholder="Enter Confirm Password"
											onChange={(e) => {
												let newPassword = formRef.current.getFieldValue('new_password');
												if (newPassword) {
													if (e.target.value && e.target.value !== newPassword) {
														setMatch(false);
													} else {
														setMatch(true);
													}
												}
												checkFormValues();
											}}
											disabled={login_method === 'google' || !newPassword || newPassValidationMsg || loadingSubmit || !user}
											visibilityToggle={{ visible: showPasswordThree, onVisibleChange: () => toggleShowPassword('confirm') }}
											onPaste={(e) => e.preventDefault()}
										/>
									</Item>
									<Item>
										<Row justify="end">
											<Button
												type="primary"
												htmlType="submit"
												style={{
													width: 100,
													marginTop: 10
												}}
												disabled={disabled || newPassValidationMsg || loadingSubmit || !user}
												loading={loadingSubmit}
											>
												Confirm
											</Button>
										</Row>
									</Item>
								</Form>
							</Col>
						</Row>
					</Card>
				</Col>
			</Row>
		</div >
	);
};

export default SettingPage;
